import { useEffect, useState, useSyncExternalStore } from 'react';
import type { ComponentType } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import type { PromptDialogProps } from './PromptDialog';
import { dialogSnapshot, registerHost, subscribeDialogs } from './dialog-store';

/**
 * 🔴 WHERE `confirmDialog` AND `promptDialog` ARE DRAWN — design-system plan DS-8. One per shell, beside the
 * `Toaster`: web/src/main.tsx (the back office and the till), web/portal/src/main.tsx, web/system/src/main.tsx, and
 * the spec harness's `WithFeedback`. It draws the first open question; the next waits until that one is answered.
 *
 * `prompt` is the prompt dialog, handed in by a shell that asks for typed answers: `<DialogHost prompt={PromptDialog} />`
 * in the back office (the till's six prompts, which must work with no network, so it is in the first load and not
 * fetched on demand) and in the harness. The portal and the console ask only yes-or-no questions and mount
 * `<DialogHost />`, so their first load does not carry the field, the number reading and the keypad; a prompt asked
 * there is refused at once (`NO_PROMPTS`).
 *
 * ⚠️ Only the FIRST host mounted draws: a spec that wraps a shell in `WithFeedback` has two, and two would draw every
 * question twice. The keys are the question's id, so a new question is a new dialog — its own focus capture, its own
 * field — never the last one's state with new words.
 *
 * DS-9a: each question carries the density of the subtree that asked it (`useConfirm`/`usePrompt` read it), and the
 * dialog is drawn at that density — the host itself sits at the root, where the page's own level applies.
 */
export function DialogHost({ prompt: Prompt }: { prompt?: ComponentType<PromptDialogProps> }) {
  const [me] = useState(() => ({}));
  const prompts = Prompt !== undefined;
  useEffect(() => registerHost(me, { prompts }), [me, prompts]);
  const { queue, host } = useSyncExternalStore(subscribeDialogs, dialogSnapshot, dialogSnapshot);
  const request = queue[0];
  if (host !== me || !request) return null;
  if (request.type === 'confirm') {
    return (
      <ConfirmDialog
        key={request.id}
        {...request.options}
        density={request.density}
        onConfirm={() => request.settle(true)}
        onCancel={() => request.settle(false)}
      />
    );
  }
  // The store refuses a prompt this host cannot draw; this only covers a host that lost its dialog while one was open.
  if (!Prompt) return null;
  return <Prompt key={request.id} {...request.options} density={request.density} onConfirm={(value) => request.settle(value)} onCancel={() => request.settle(null)} />;
}
